import { useQuery } from "@tanstack/react-query";
import {
  FileChartColumnIncreasing,
  FolderKanban,
  LayoutTemplateIcon,
  Grid3x3Icon,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { useTenant } from "@/domains/tenant/hooks";
import { DashboardSkeleton } from "./components/dashboard-skeleton";
import { DashboardKpiCard } from "./components/dashboard-kpi-card";
import { DashboardReportsSection } from "./components/dashboard-reports-section";
import { DashboardMatricesSection } from "./components/dashboard-matrices-section";
import { DashboardTemplatesSection } from "./components/dashboard-templates-section";
import { DashboardResourcesSection } from "./components/dashboard-resources-section";
import { dashboardQueries } from "./queries/dashboard";
import { useDashboardViewPreferences } from "./hooks/use-dashboard-view-preferences";

export function DashboardPage() {
  const { data, isLoading, isError, error } = useQuery(dashboardQueries.data());
  const { data: tenantData } = useTenant();
  const { preferences, setView } = useDashboardViewPreferences();
  const hasTemplateBuilder =
    tenantData?.setting?.setting_features_enum?.includes("report-builder") ??
    false;

  return (
    <>
      <title>Dashboard - Researchly</title>
      <div className="flex flex-col gap-8 p-4 sm:p-6">
        <div className="space-y-2">
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            Dashboard
          </h1>
          <p className="max-w-3xl text-base text-muted-foreground">
            Your recent work and key activity across Reports, Matrices,
            Templates, and Resources.
          </p>
        </div>

        {isError ? (
          <Alert variant="destructive">
            <AlertTitle>Dashboard unavailable</AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : "The dashboard data could not be loaded."}
            </AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <DashboardSkeleton hasTemplateBuilder={hasTemplateBuilder} />
        ) : data ? (
          <div className="space-y-8">
            <div
              className={`grid gap-4 sm:grid-cols-2 ${
                hasTemplateBuilder ? "lg:grid-cols-4" : "lg:grid-cols-3"
              }`}
            >
              <DashboardKpiCard
                label="Reports"
                count={data.reportCount}
                icon={FileChartColumnIncreasing}
              />
              <DashboardKpiCard
                label="Matrices"
                count={data.matrixCount}
                icon={Grid3x3Icon}
              />
              {hasTemplateBuilder ? (
                <DashboardKpiCard
                  label="Templates"
                  count={data.templateCount}
                  icon={LayoutTemplateIcon}
                />
              ) : null}
              <DashboardKpiCard
                label="Resources"
                count={data.resourceCount}
                icon={FolderKanban}
              />
            </div>

            <DashboardReportsSection
              items={data.reports}
              view={preferences.reports}
              onViewChange={(value) => setView("reports", value)}
            />
            <DashboardMatricesSection
              items={data.matrices}
              view={preferences.matrices}
              onViewChange={(value) => setView("matrices", value)}
            />
            {hasTemplateBuilder ? (
              <DashboardTemplatesSection
                items={data.templates}
                view={preferences.templates}
                onViewChange={(value) => setView("templates", value)}
              />
            ) : null}
            <DashboardResourcesSection
              items={data.resources}
              view={preferences.resources}
              onViewChange={(value) => setView("resources", value)}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
